import { makeAutoObservable } from 'mobx'
import Loader from 'reusable/mobx-helpers/Loader'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { apiClient } from 'api/trpc/trpc-client'
import { messageConfirm } from 'reusable/mui/modals/message-confirm'
import { snackbarSuccess, snackbarError } from 'reusable/mui/snackbar/snackbar'
import { ChargebeeDto } from '@gap/backend/app-services/chargebee/models/ChargebeeDto'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'
import { openChargebeeCheckout, closeChargebeeModals } from './chargebee-loader'
import { executeLoading } from 'reusable/mui/modals/execute-loading'
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils";
import type { OrgSubscriptionTierStore } from 'features/main/main-router/org-guard/main-container/org-subscription-tiers/OrgSubscriptionTierStore'

const isIndividualPlan = (plan: ChargebeeDto.PurchasableItem): boolean => plan.planType === 'individual'

// Sort plans by price ascending, but always push custom plans (no list price, e.g. Enterprise) to the far right.
const comparePlans = (planA: ChargebeeDto.PurchasableItem, planB: ChargebeeDto.PurchasableItem): number => {
   if (!!planA.isCustom !== !!planB.isCustom) {
      return planA.isCustom ? 1 : -1
   }
   return (planA.price ?? Infinity) - (planB.price ?? Infinity)
}

export class BillingStore {
   sp
   loader = new Loader(false)
   checkoutLoader = new Loader(false)
   billing: ChargebeeDto.OrgBilling | null = null
   plans: ChargebeeDto.PurchasableItem[] = []
   plansDialog = new ModalState({ onClose: () => this.plansDialogClosed() })
   plansDialogTitle = 'Change plan'
   private selectedPlanTabIndex: number | undefined = undefined
   private onPlansDialogClosed: (() => void) | undefined = undefined

   constructor(sp: { orgGuardStore: OrgGuardStore; orgSubscriptionTierStore: OrgSubscriptionTierStore; onPlanChanged?: () => void }) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get org() {
      return this.sp.orgGuardStore.org
   }

   get subscription() {
      return this.billing?.subscription
   }

   /** A trial has no plan type and an Individual plan is not a team, so only a team (or custom) plan counts. */
   get isOnTeamPlan(): boolean {
      return this.billing?.plan?.planType === 'team'
   }

   /** The tab the picker opens on: Multi-user for an org already on a team plan, else Individual. */
   get defaultPlanTabIndex(): number {
      return this.isOnTeamPlan ? 1 : 0
   }

   get planTabIndex(): number {
      return this.selectedPlanTabIndex ?? this.defaultPlanTabIndex
   }

   /** A paid plan or a converted trial: no longer a new buyer, so plans are not promoted to them. */
   get isOnPaidPlan(): boolean {
      return !!this.subscription && !this.subscription.isTrialPlan
   }

   get canManageSeats(): boolean {
      return this.sp.orgSubscriptionTierStore.canManageSeats
   }

   get currentPlanItemPriceId() {
      return this.subscription?.items.find(item => item.itemType === 'plan')?.itemPriceId
   }

   get currentPlanPrice() {
      return this.plans.find(plan => plan.itemPriceId === this.currentPlanItemPriceId)?.price ?? 0
   }

   get individualPlans() {
      return this.plans.filter(isIndividualPlan)
   }

   get multiUserPlans() {
      return this.plans.filter(plan => !isIndividualPlan(plan))
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         const [plans, billing] = await Promise.all([apiClient.chargebee.listPlans.query(), apiClient.chargebee.getMyOrgBilling.query()])
         this.plans = [...plans].sort(comparePlans)
         this.billing = billing
      })
   }

   /** Loads the plans and the org's billing, then opens the plan picker; `onClosed` runs once when it closes. */
   openChangePlan = async (params: { title: string; onClosed?: () => void }) => {
      this.plansDialogTitle = params.title
      this.onPlansDialogClosed = params.onClosed
      this.selectedPlanTabIndex = undefined
      const loaded = await executeLoading('Loading plans…', this.fetchReportingSuccess)
      if (loaded) {
         this.plansDialog.open()
      } else {
         snackbarError('We had trouble loading the plans. Please try again.')
      }
   }

   /** Opens checkout on the current plan, where the advisor raises seat quantities or adds the recommended seat add-ons. */
   addSeats = async () => {
      await this.checkoutLoader.executeLoading(async () => {
         try {
            await openChargebeeCheckout({
               createHostedPage: () => apiClient.chargebee.createAddSeatsCheckout.mutate(),
               onSuccess: this.onChangePlanSuccess,
            })
         } catch {
            snackbarError('We had trouble opening checkout. Please try again.')
         }
      })
   }

   changePlanTab = (_event: React.SyntheticEvent, tabIndex: number) => {
      this.selectedPlanTabIndex = tabIndex
   }

   changePlanButtonHandler = (event: React.MouseEvent<HTMLElement>) => {
      const itemPriceId = event.currentTarget.getAttribute('data-item-price-id')
      if (itemPriceId) {
         this.changePlan(itemPriceId)
      }
   }

   requestCustomPlanButtonHandler = (event: React.MouseEvent<HTMLElement>) => {
      const itemPriceId = event.currentTarget.getAttribute('data-item-price-id')
      if (itemPriceId) {
         this.requestCustomPlan(itemPriceId)
      }
   }

   // Custom plans (e.g. Enterprise).
   requestCustomPlan = async (itemPriceId: string) => {
      const confirmed = await messageConfirm(
         "You'll switch to the Enterprise plan right away, unlocking its included seats. Our team will be in touch to agree pricing and set up your billing. Are you sure you want to move to Enterprise?",
         'Move to Enterprise?',
         { confirmButtonTitle: 'Move to Enterprise' }
      )
      if (!confirmed) {
         return
      }
      await this.checkoutLoader.executeLoading(async () => {
         try {
            await apiClient.chargebee.requestCustomPlan.mutate({ itemPriceId })
            this.onChangePlanSuccess()
            snackbarSuccess("You're on the Enterprise plan. Our team will be in touch.")
         } catch {
            snackbarError('We had trouble moving you to the Enterprise plan. Please try again.')
         }
      })
   }

   changePlan = async (itemPriceId: string) => {
      await this.checkoutLoader.executeLoading(async () => {
         try {
            await openChargebeeCheckout({
               createHostedPage: () => apiClient.chargebee.createPlanChangeCheckout.mutate({ itemPriceId }),
               onSuccess: this.onChangePlanSuccess,
            })
         } catch {
            snackbarError('We had trouble opening checkout. Please try again.')
         }
      })
   }

   onChangePlanSuccess = () => {
      closeChargebeeModals()
      this.sp.onPlanChanged?.()
      this.plansDialog.close()
      executeLoading('Updating your plan…', async () => {
         try {
            await this.sp.orgGuardStore.refreshChargebeeSeatGrant()
         } catch {
            // ignore — best-effort refresh; the webhook reconciles the persisted seat grant later.
         }
      })
   }

   /** `executeLoading` never settles on a thrown error, so failure comes back as `false` instead. */
   private fetchReportingSuccess = async (): Promise<boolean> => {
      try {
         await this.fetch()
         return true
      } catch {
         return false
      }
   }

   private plansDialogClosed = () => {
      const onClosed = this.onPlansDialogClosed
      this.onPlansDialogClosed = undefined
      onClosed?.()
   }
}

export const { Provider: BillingStoreProvider, useProvidedStore: useBillingStore } = createStoreContext(BillingStore)
